/**
 * Onboarding-feature API layer + i18n namespace registration.
 *
 * The 5-step onboarding flow (Welcome → Phone → OTP → Basics → Done) drives
 * three backend calls — `phoneSignupStart`, `phoneSignupVerify`, and
 * `createProject` — all defined once in `@/lib/garden-api` (the shared typed
 * client; we do NOT recreate the fetchers here). This module:
 *
 *   1. Registers the `onboarding` i18n namespace (ru/ky/en) on the shared
 *      i18next singleton via `addResourceBundle` — mirroring the account
 *      feature's `registerAccountI18n()` (we never touch `lib/i18n.ts`). All
 *      three locales ship per ADR-028; a missing KY/EN key falls back to RU
 *      (REQ-PRD-§13.2-A).
 *   2. Exposes thin TanStack Query `useMutation` hooks around the garden-api
 *      fetchers so the step components get `isPending`/`error`/`mutateAsync`
 *      without re-implementing request plumbing (the api-client already does
 *      in-memory JWT, Idempotency-Key, 401→refresh→retry, RFC-9457 parsing).
 *   3. Maps the design's display labels (Хрущёвка / Бишкек / "В этом месяце")
 *      to the API enums (`khrushchevka` / `bishkek` / `this_month`) so the
 *      Basics step can build a typed `ProjectCreateRequest`.
 */
import { useMutation, type UseMutationResult } from "@tanstack/react-query";

import {
  createProject,
  emailSignup,
  emailVerify,
  phoneSignupStart,
  phoneSignupVerify,
  type EmailSignupRequest,
  type EmailSignupResponse,
  type EmailVerifyRequest,
  type EmailVerifyResponse,
  type HousingType,
  type PhoneSignupStartRequest,
  type PhoneSignupStartResponse,
  type PhoneSignupVerifyRequest,
  type PhoneSignupVerifyResponse,
  type ProjectCity,
  type ProjectCreateRequest,
  type ProjectResponse,
  type ProjectTimeline,
} from "@/lib/garden-api";
import i18n from "@/lib/i18n";

import onboardingEn from "@/locales/en/onboarding.json";
import onboardingKy from "@/locales/ky/onboarding.json";
import onboardingRu from "@/locales/ru/onboarding.json";

export const ONBOARDING_NS = "onboarding";

let onboardingI18nRegistered = false;

/**
 * Register the `onboarding` i18n namespace (ru/ky/en) on the shared i18next
 * singleton. Idempotent + init-safe (guards on `isInitialized` and the
 * method's presence). Called at the top of the flow's render so the keys are
 * present regardless of import order — exactly the account feature's idiom.
 *
 * `deep=true` (4th arg) merges nested objects; `overwrite=false` (5th) keeps
 * an existing bundle (HMR-safe).
 */
export function registerOnboardingI18n(): void {
  if (onboardingI18nRegistered) return;
  if (!i18n.isInitialized || typeof i18n.addResourceBundle !== "function") {
    return;
  }
  i18n.addResourceBundle("ru", ONBOARDING_NS, onboardingRu, true, false);
  i18n.addResourceBundle("ky", ONBOARDING_NS, onboardingKy, true, false);
  i18n.addResourceBundle("en", ONBOARDING_NS, onboardingEn, true, false);
  onboardingI18nRegistered = true;
}

// Best-effort eager registration for the production path (initI18n() runs in
// main.tsx before feature modules import). In tests where init happens in
// beforeEach, the flow calls registerOnboardingI18n() on render.
registerOnboardingI18n();

// ─────────────────────────── label → enum maps ───────────────────────────

/**
 * Property-type pills, in the design's display order. Each carries its API
 * enum so the Basics step can submit the right `housing_type`. The `labelKey`
 * resolves against the `onboarding` namespace.
 */
export interface HousingTypeOption {
  value: HousingType;
  labelKey: string;
}

export const HOUSING_TYPE_OPTIONS: readonly HousingTypeOption[] = [
  { value: "khrushchevka", labelKey: "basics.types.khrushchevka" },
  { value: "new_build", labelKey: "basics.types.new_build" },
  { value: "private_house", labelKey: "basics.types.private_house" },
] as const;

/** City segmented options, design order (Бишкек / Ош). */
export interface CityOption {
  value: ProjectCity;
  labelKey: string;
}

export const CITY_OPTIONS: readonly CityOption[] = [
  { value: "bishkek", labelKey: "basics.cities.bishkek" },
  { value: "osh", labelKey: "basics.cities.osh" },
] as const;

/** Timeline pills, design order (В этом месяце / Через 1–3 месяца / Позже). */
export interface TimelineOption {
  value: ProjectTimeline;
  labelKey: string;
}

export const TIMELINE_OPTIONS: readonly TimelineOption[] = [
  { value: "this_month", labelKey: "basics.whens.this_month" },
  { value: "1_3_months", labelKey: "basics.whens.1_3_months" },
  { value: "later", labelKey: "basics.whens.later" },
] as const;

// ─────────────────────────── mutation hooks ───────────────────────────

/**
 * POST /auth/phone-signup/start — sends the SMS code. Returns the throttle
 * window; the OTP step starts the resend countdown from it. No query key (a
 * mutation, not cached server state).
 */
export function usePhoneSignupStart(): UseMutationResult<
  PhoneSignupStartResponse,
  Error,
  PhoneSignupStartRequest
> {
  return useMutation<PhoneSignupStartResponse, Error, PhoneSignupStartRequest>({
    mutationFn: (body) => phoneSignupStart(body),
  });
}

/**
 * POST /auth/phone-signup/verify — verifies the 6-digit code. On success the
 * caller sets `access_token` into auth-store (the HttpOnly refresh cookie is
 * set server-side). Wrong code throws an `ApiError` the OTP step maps to a
 * field error.
 */
export function usePhoneSignupVerify(): UseMutationResult<
  PhoneSignupVerifyResponse,
  Error,
  PhoneSignupVerifyRequest
> {
  return useMutation<PhoneSignupVerifyResponse, Error, PhoneSignupVerifyRequest>({
    mutationFn: (body) => phoneSignupVerify(body),
  });
}

/**
 * POST /auth/signup — creates the account and sends the email OTP. Returns the
 * new `user_id` (passed to verify) and, in non-prod, the echoed `dev_otp` so
 * the OTP step can show the code on-screen (no real email delivery in QA).
 */
export function useEmailSignup(): UseMutationResult<
  EmailSignupResponse,
  Error,
  EmailSignupRequest
> {
  return useMutation<EmailSignupResponse, Error, EmailSignupRequest>({
    mutationFn: (body) => emailSignup(body),
  });
}

/**
 * POST /auth/email/verify — verifies the 6-digit code. On success the caller
 * stores `access_token` into auth-store (the HttpOnly refresh cookie is set
 * server-side). Wrong/expired code throws an `ApiError` the OTP step maps to
 * an inline error.
 */
export function useEmailVerify(): UseMutationResult<
  EmailVerifyResponse,
  Error,
  EmailVerifyRequest
> {
  return useMutation<EmailVerifyResponse, Error, EmailVerifyRequest>({
    mutationFn: (body) => emailVerify(body),
  });
}

/**
 * POST /projects — creates the renovation project from the Basics step. The
 * server serialises Numeric columns as strings; the request accepts plain
 * numbers (area/budget) which the api-client JSON-encodes verbatim.
 */
export function useCreateProject(): UseMutationResult<
  ProjectResponse,
  Error,
  ProjectCreateRequest
> {
  return useMutation<ProjectResponse, Error, ProjectCreateRequest>({
    mutationFn: (body) => createProject(body),
  });
}
